import api from '@anm/api';
import calcPricingPlanData from '@anm/helpers/pricing/calcPricingPlanData';
import asyncEntity from '@anm/helpers/redux/asyncEntity';
import { call, put, select, take } from '@anm/helpers/saga/effects';
import { takeType } from '@anm/helpers/saga/typesafe-actions';
import { Features } from '@anm/shared/features';

import vtConfig from '../../../config';
import { authActions } from '../auth';
import { subscriptionActions, subscriptionSelectors } from '../subscription';
import { userActions, userSelectors } from '../user';

import { pricingActions, pricingSelectors } from '.';

const { sale } = vtConfig();

const fetchPricingContent = asyncEntity(pricingActions.fetchPricingContentAsync, () =>
  api().pricing.getPricingContent()
);

function* getSale() {
  const isUserNew = yield* select(userSelectors.selectIsUserNew);

  if (!isUserNew) return;

  yield put(pricingActions.setSale(sale || null));
}

function* watchFetchSale() {
  while (true) {
    yield take(takeType(pricingActions.fetchSale));

    yield call(getSale);
  }
}

function* watchFetchPricingContent() {
  while (true) {
    yield take(takeType(pricingActions.fetchPricingContent));
    yield call(fetchPricingContent);
  }
}

function* watchFetchPricingSubscriptionData() {
  while (true) {
    yield take(takeType(pricingActions.fetchPricingSubscriptionData));

    yield put(pricingActions.fetchPricingContent());
    yield put(pricingActions.fetchPricingSubscriptionDataAsync.request({}));
    yield put(subscriptionActions.fetchSubscriptionList());
  }
}

function* setPricingSubscriptionData() {
  const currentSubscription = yield* select(userSelectors.selectWaveSubscription);

  const planList = yield* select(subscriptionSelectors.selectSubscriptionList);
  const saleDiscount = yield* select(pricingSelectors.selectCurrentSaleDiscount);
  const pricingContent = yield* select(pricingSelectors.selectPricingContent);
  const currentPeriod = yield* select(pricingSelectors.selectCurrentPeriod);
  const isUserNew = yield* select(userSelectors.selectIsUserNew);
  const teamPermissions = yield* select(userSelectors.selectTeamPermissions);

  if (!planList || !pricingContent) {
    return null;
  }

  const data = calcPricingPlanData({
    teamPermissions,
    currentSubscription,
    view: currentPeriod,
    discount: isUserNew && saleDiscount ? saleDiscount : undefined,
    planContentList: pricingContent.plans,
    subscriptionList: planList.filter(({ name }) => Features.teamsEnabled || !name.includes('Team'))
  });

  yield put(pricingActions.fetchPricingSubscriptionDataAsync.success({ plans: data }));
}

function* watchFetchPricingSubscriptionDataSuccess() {
  while (true) {
    yield take([
      pricingActions.setSale,
      authActions.logoutAsync.success,
      userActions.updateAsync.success,
      userActions.getUserAsync.success,
      pricingActions.updatePricingMeta,
      pricingActions.fetchPricingContentAsync.success,
      subscriptionActions.fetchSubscriptionListAsync.success
    ]);

    yield call(setPricingSubscriptionData);
  }
}

function* watchChangePlanPeriodView() {
  while (true) {
    const { payload: currentPeriod }: ReturnType<typeof pricingActions.changePlanPeriodView> = yield take(
      pricingActions.changePlanPeriodView
    );

    yield put(pricingActions.updatePricingMeta({ currentPeriod }));
    yield put(pricingActions.fetchSale());
  }
}

export default () => [
  call(watchFetchSale),
  call(watchFetchPricingContent),
  call(watchChangePlanPeriodView),
  call(watchFetchPricingSubscriptionData),
  call(watchFetchPricingSubscriptionDataSuccess)
];
